import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Star, MapPin, Clock, Phone, Mail, ChevronLeft, Wifi, UtensilsCrossed, Waves, Dumbbell, Car, Shield, Sparkles } from 'lucide-react';
import { SearchType } from '../types';
import type { SearchQuery } from '../types';

interface HotelPageData {
    id: string;
    name: string;
    slug: string;
    stars: number;
    rating: number;
    description: string;
    descriptionSv: string;
    destination: string;
    country: string;
    heroImage: string;
    images: Array<{ url: string; alt?: string }>;
    rooms: Array<{
        name: string;
        description: string;
        images: Array<{ url: string }>;
        boardTypes: string[];
    }>;
    facilities: Array<string | { name: string }>;
    location: { lat?: number; lng?: number };
    checkInTime: string;
    checkOutTime: string;
    contact: { phone?: string; email?: string };
}

const HotelLandingPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [hotel, setHotel] = useState<HotelPageData | null>(null);
    const [meta, setMeta] = useState<{ title: string; description: string; ogImage: string } | null>(null);
    const [schemaOrg, setSchemaOrg] = useState<object | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeImageIdx, setActiveImageIdx] = useState(0);

    // Auto-redirect to search results when arriving from Reisegiganten book_url with query params
    useEffect(() => {
        const hotelId = searchParams.get('hotelId');
        const checkIn = searchParams.get('checkIn');
        if (hotelId && checkIn) {
            const nights = parseInt(searchParams.get('nights') || '7');
            const adults = parseInt(searchParams.get('adults') || '2');
            const childAgesStr = searchParams.get('childAges') || '';
            const childrenAges = childAgesStr ? childAgesStr.split(',').map(Number).filter(n => !isNaN(n)) : [];

            // Reisegiganten book_url parameters for pre-selection
            const departure = searchParams.get('departure') || '';        // Tourvisio numeric ID e.g. 10
            const departureName = searchParams.get('departureName') || '';  // City name e.g. Stockholm
            const arrivalId = searchParams.get('arrivalId') || '';         // Tourvisio numeric ID e.g. 6
            const arrivalName = searchParams.get('arrivalName') || '';     // Destination name e.g. Antalya
            const roomType = searchParams.get('roomType') || '';           // e.g. Standard Room
            const board = searchParams.get('board') || '';                  // e.g. All Inclusive
            const boardCode = searchParams.get('boardCode') || '';         // e.g. AI
            const offerId = searchParams.get('offerId') || '';             // Exact offer ID

            const autoQuery: SearchQuery = {
                type: SearchType.PackageTours,
                destinationId: arrivalId,
                destinationName: arrivalName,
                departureId: departure,
                departureName: departureName,
                hotelId,
                checkIn,
                nights,
                rooms: 1,
                nationality: 'SE',
                currency: 'SEK',
                adults,
                children: childrenAges.length,
                childrenAges,
            };
            navigate('/results', {
                state: {
                    searchQuery: autoQuery,
                    from: '/holidays',
                    // Reisegiganten pre-selection hints
                    reisegiganten: {
                        departureCode: departure,
                        departureName,
                        roomType,
                        board,
                        boardCode,
                        offerId,
                    }
                },
                replace: true
            });
            return;
        }
    }, [searchParams, navigate]);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        fetch(`/api/hotels/page/${slug}`)
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setHotel(data.hotel);
                    setMeta(data.meta);
                    setSchemaOrg(data.schemaOrg);

                    // Set page meta tags for SEO
                    document.title = data.meta.title;
                    let metaDesc = document.querySelector('meta[name="description"]');
                    if (!metaDesc) {
                        metaDesc = document.createElement('meta');
                        metaDesc.setAttribute('name', 'description');
                        document.head.appendChild(metaDesc);
                    }
                    metaDesc.setAttribute('content', data.meta.description);

                    // OG tags
                    const setOgTag = (prop: string, content: string) => {
                        let tag = document.querySelector(`meta[property="${prop}"]`);
                        if (!tag) {
                            tag = document.createElement('meta');
                            tag.setAttribute('property', prop);
                            document.head.appendChild(tag);
                        }
                        tag.setAttribute('content', content);
                    };
                    setOgTag('og:title', data.meta.title);
                    setOgTag('og:description', data.meta.description);
                    setOgTag('og:image', data.meta.ogImage);
                    setOgTag('og:url', `https://lumitours.se/hotels/${slug}`);
                    setOgTag('og:type', 'hotel');

                    // Schema.org JSON-LD
                    let script = document.getElementById('hotel-schema');
                    if (!script) {
                        script = document.createElement('script');
                        script.id = 'hotel-schema';
                        script.setAttribute('type', 'application/ld+json');
                        document.head.appendChild(script);
                    }
                    script.textContent = JSON.stringify(data.schemaOrg);
                } else {
                    setError(data.error || 'Hotel not found');
                }
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));

        return () => {
            // Cleanup schema on unmount
            const script = document.getElementById('hotel-schema');
            if (script) script.remove();
        };
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4" />
                    <p className="text-gray-600">Loading hotel details...</p>
                </div>
            </div>
        );
    }

    if (error || !hotel) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center max-w-md">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Hotel Not Found</h1>
                    <p className="text-gray-600 mb-6">{error || 'The hotel page you are looking for does not exist.'}</p>
                    <Link to="/holidays" className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all">
                        Browse Holidays
                    </Link>
                </div>
            </div>
        );
    }

    const facilityNames = hotel.facilities.map(f => typeof f === 'string' ? f : (f as any).name || '');

    const facilityIcon = (name: string) => {
        const lower = name.toLowerCase();
        if (lower.includes('wifi') || lower.includes('internet')) return <Wifi size={18} />;
        if (lower.includes('restaurant') || lower.includes('food') || lower.includes('bar')) return <UtensilsCrossed size={18} />;
        if (lower.includes('pool') || lower.includes('swim') || lower.includes('beach')) return <Waves size={18} />;
        if (lower.includes('gym') || lower.includes('fitness') || lower.includes('sport')) return <Dumbbell size={18} />;
        if (lower.includes('parking') || lower.includes('transfer')) return <Car size={18} />;
        if (lower.includes('safe') || lower.includes('security')) return <Shield size={18} />;
        return <Sparkles size={18} />;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
                {hotel.heroImage ? (
                    <img
                        src={hotel.heroImage}
                        alt={hotel.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-indigo-800" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                {/* Back Button */}
                <Link
                    to="/holidays"
                    className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/30 transition-all"
                >
                    <ChevronLeft size={18} />
                    <span className="text-sm font-medium">Back</span>
                </Link>

                {/* Hotel Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                    <div className="container mx-auto max-w-6xl">
                        <div className="flex items-center gap-2 mb-3">
                            {Array.from({ length: hotel.stars || 0 }).map((_, i) => (
                                <Star key={i} size={18} fill="#fbbf24" className="text-amber-400" />
                            ))}
                            {hotel.rating > 0 && (
                                <span className="ml-2 px-3 py-1 bg-amber-400 text-black rounded-full text-sm font-bold">
                                    {hotel.rating.toFixed(1)}
                                </span>
                            )}
                        </div>
                        <h1 className="text-3xl md:text-5xl font-black text-white mb-2">{hotel.name}</h1>
                        <div className="flex items-center gap-2 text-white/80">
                            <MapPin size={18} />
                            <span className="text-lg">{hotel.destination}{hotel.country ? `, ${hotel.country}` : ''}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto max-w-6xl px-4 md:px-8 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-10">
                        {/* Description */}
                        {(hotel.description || hotel.descriptionSv) && (
                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">About This Hotel</h2>
                                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                                    <p>{hotel.descriptionSv || hotel.description}</p>
                                </div>
                            </section>
                        )}

                        {/* Image Gallery */}
                        {hotel.images.length > 0 && (
                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Gallery</h2>
                                <div className="relative rounded-2xl overflow-hidden mb-4">
                                    <img
                                        src={typeof hotel.images[activeImageIdx] === 'string' ? hotel.images[activeImageIdx] : (hotel.images[activeImageIdx] as any)?.url}
                                        alt={`${hotel.name} - Image ${activeImageIdx + 1}`}
                                        className="w-full h-[400px] object-cover"
                                    />
                                </div>
                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {hotel.images.slice(0, 10).map((img, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setActiveImageIdx(i)}
                                            className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${i === activeImageIdx ? 'border-purple-500 scale-105' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                        >
                                            <img
                                                src={typeof img === 'string' ? img : (img as any)?.url}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Room Types */}
                        {hotel.rooms.length > 0 && (
                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Room Types</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {hotel.rooms.map((room, i) => (
                                        <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                                            {room.images?.[0] && (
                                                <img
                                                    src={typeof room.images[0] === 'string' ? room.images[0] : (room.images[0] as any)?.url}
                                                    alt={room.name}
                                                    className="w-full h-40 object-cover"
                                                />
                                            )}
                                            <div className="p-4">
                                                <h3 className="font-bold text-gray-900 mb-1">{room.name}</h3>
                                                {room.description && (
                                                    <p className="text-sm text-gray-600 line-clamp-2">{room.description}</p>
                                                )}
                                                {room.boardTypes.length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-1">
                                                        {room.boardTypes.map((bt, j) => (
                                                            <span key={j} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                                                                {bt}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Facilities */}
                        {facilityNames.length > 0 && (
                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Facilities & Amenities</h2>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {facilityNames.filter(Boolean).map((name, i) => (
                                        <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100">
                                            <div className="text-purple-500">{facilityIcon(name)}</div>
                                            <span className="text-sm text-gray-700">{name}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Book CTA */}
                        <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-6 text-white sticky top-24">
                            <h3 className="text-xl font-bold mb-2">Book This Hotel</h3>
                            <p className="text-white/80 text-sm mb-6">Search for the best available rates and packages.</p>
                            <Link
                                to={`/holidays?hotel=${hotel.id}`}
                                className="block w-full text-center py-3 bg-white text-purple-700 rounded-xl font-bold hover:bg-gray-100 transition-all"
                            >
                                Search Availability
                            </Link>
                        </div>

                        {/* Hotel Info Card */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
                            <h3 className="font-bold text-gray-900">Hotel Information</h3>

                            <div className="flex items-center gap-3 text-sm text-gray-600">
                                <Clock size={16} className="text-gray-400" />
                                <div>
                                    <p>Check-in: <span className="font-medium text-gray-900">{hotel.checkInTime}</span></p>
                                    <p>Check-out: <span className="font-medium text-gray-900">{hotel.checkOutTime}</span></p>
                                </div>
                            </div>

                            {hotel.contact?.phone && (
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                    <Phone size={16} className="text-gray-400" />
                                    <span>{hotel.contact.phone}</span>
                                </div>
                            )}

                            {hotel.contact?.email && (
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                    <Mail size={16} className="text-gray-400" />
                                    <span>{hotel.contact.email}</span>
                                </div>
                            )}

                            <div className="flex items-center gap-3 text-sm text-gray-600">
                                <MapPin size={16} className="text-gray-400" />
                                <span>{hotel.destination}{hotel.country ? `, ${hotel.country}` : ''}</span>
                            </div>
                        </div>

                        {/* AI Planning CTA */}
                        <Link
                            to="/tour-assist"
                            className="flex items-center gap-3 p-4 bg-gradient-to-r from-rose-500 via-purple-500 to-indigo-500 rounded-2xl text-white hover:scale-[1.02] transition-all shadow-lg shadow-purple-500/25"
                        >
                            <Sparkles size={20} />
                            <div>
                                <p className="font-bold text-sm">Plan with Petra AI</p>
                                <p className="text-xs text-white/80">Get personalized travel advice</p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HotelLandingPage;
